import React, { useCallback, useEffect, useRef } from 'react';
import './ComingSoonModal.css';

/**
 * Teaser for an experience that is not open yet.
 *
 * This is a promo, not a gate: everything currently on offer is still bookable,
 * so every way out of the dialog carries on into game selection rather than
 * turning the visitor away. The copy has to say so explicitly — a panel headed
 * "Coming Soon" reads as "nothing is available" unless told otherwise.
 *
 * The video comes from REACT_APP_COMING_SOON_VIDEO_URL, and that variable is
 * also the switch: with no URL configured there is nothing to tease, so the
 * Welcome page goes straight to game selection. Retiring the teaser is a
 * deploy-time change, not a code change.
 */

const RAW_URL = (process.env.REACT_APP_COMING_SOON_VIDEO_URL || '').trim();

/** True when a teaser video is configured. */
export const isComingSoonEnabled = Boolean(RAW_URL);

const SEEN_KEY = 'immersia_coming_soon_seen';

/**
 * True the first time it is called in a browsing session, false afterwards.
 *
 * The teaser is worth one interruption, not one per click — someone who backs
 * out of game selection and taps "Let's Play" again wants the games, not the
 * video a second time. sessionStorage rather than localStorage so a later visit
 * sees it again, which matters while the promo is still current.
 *
 * Degrades to showing every time if storage is unavailable (Safari private
 * mode throws on write); an over-eager teaser beats a crash.
 */
export const claimComingSoonOncePerSession = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.sessionStorage.getItem(SEEN_KEY)) return false;
    window.sessionStorage.setItem(SEEN_KEY, '1');
  } catch {
    // Storage disabled — fall through and show it.
  }
  return true;
};

/**
 * YouTube links must go through an iframe — a <video> element cannot play a
 * watch page. Everything else is treated as a direct media file.
 */
const youTubeId = (url: string): string | null => {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/
  );
  return match ? match[1] : null;
};

const VIDEO_ID = youTubeId(RAW_URL);

const EMBED_URL = VIDEO_ID
  ? `https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1&playsinline=1`
  : '';

interface Props {
  onClose: () => void;
}

const ComingSoonModal: React.FC<Props> = ({ onClose }) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  /** Whatever had focus before the dialog opened, so it can be handed back. */
  const openerRef = useRef<Element | null>(null);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    openerRef.current = document.activeElement;
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);

    // Stop the page behind the dialog scrolling under it on mobile.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
    };
  }, [handleClose]);

  /**
   * Opening the dialog is itself a click, so the browser grants autoplay with
   * sound. Some do refuse anyway (iOS low-power mode, stricter settings) — fall
   * back to a muted play rather than showing a teaser that never starts.
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.play().catch(() => {
      video.muted = true;
      video.play().catch(() => {
        // Controls are visible; the visitor can start it themselves.
      });
    });
  }, []);

  return (
    <div
      className="coming-soon-backdrop"
      role="presentation"
      /* Only a click that both starts and ends on the backdrop closes, so a
         drag that happens to release outside the panel does not dismiss it. */
      onClick={event => {
        if (event.target === event.currentTarget) handleClose();
      }}
    >
      <div
        className="coming-soon-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="coming-soon-title"
        aria-describedby="coming-soon-copy"
      >
        <button
          ref={closeRef}
          type="button"
          className="coming-soon-close"
          onClick={handleClose}
          aria-label="Close and continue"
        >
          &times;
        </button>

        <div className="coming-soon-head">
          <span className="coming-soon-eyebrow">Coming Soon</span>
          <h2 id="coming-soon-title">Something new is landing at Immersia</h2>
          <p id="coming-soon-copy">
            Here is a first look at what we are building. Everything else is open as usual —
            close this to browse the games and book your session now.
          </p>
        </div>

        <div className="coming-soon-media">
          {VIDEO_ID ? (
            <iframe
              src={EMBED_URL}
              title="Immersia — coming soon"
              /* autoplay must be granted explicitly to a cross-origin frame */
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              frameBorder="0"
            />
          ) : (
            <video
              ref={videoRef}
              src={RAW_URL}
              controls
              playsInline
              preload="metadata"
            />
          )}
        </div>

        <button type="button" className="coming-soon-dismiss" onClick={handleClose}>
          Got it
        </button>
      </div>
    </div>
  );
};

export default ComingSoonModal;
